/**
 * SVG-Map — selbst generierte Sambia-Karte, kein OSM/Leaflet, keine externen
 * Requests, keine Tiles.
 *
 * Projektion (FEST — muss exakt mit dem Umriss-Pfad in _zambia_map.html.twig
 * übereinstimmen, sonst landen die Pins falsch):
 *   äquirektangulär über Sambias Bounding-Box, cos-Korrektur der Länge.
 *   SVG viewBox: "0 0 1149 990".
 *
 * Markup:
 *   Projektkarte (project()-Pins über lat/lng):
 *     <div class="ds-projectmap" data-projectmap-pins='[{lat,lng,title,location,status,url}]'>
 *       <svg class="ds-map__svg" …>…<g class="ds-map__pins"></g></svg>
 *     </div>
 *   Reisekarte (nummerierte Etappen, lat/lon):
 *     <div class="ds-travelmap" data-travelmap-stations='[{name,lat,lon,date}]'>
 *       <svg class="ds-map__svg" …>…<g class="ds-map__lines"></g><g class="ds-map__pins"></g></svg>
 *     </div>
 *
 * Tooltip-Verhalten:
 *   - Maus: Hover öffnet, Klick toggled (sticky), Mouseleave schließt NUR wenn nicht sticky.
 *   - Touch / Keyboard: Klick/Enter/Space toggled den Tooltip (sticky).
 *   - Immer nur ein Tooltip gleichzeitig aktiv (andere werden geschlossen).
 *   - Escape schließt den aktiven Tooltip.
 *   - Klick außerhalb des aktiven Pins schließt den Tooltip.
 *   - aria-expanded auf jedem Pin-Element (a11y).
 */

const LNG_MIN = 21.9; // West → Ost
const LAT_MAX = -8.2; // Nord (oben) → Süd (unten)
const PIX = 100;
const COS_MID = 0.9737; // cos(13.15°), mittlere Breite

/** Projiziert geografische Koordinaten in das SVG-Koordinatensystem. */
function project(lng, lat) {
  return {
    x: (lng - LNG_MIN) * PIX * COS_MID, // 0 … ~1149
    y: (LAT_MAX - lat) * PIX, // 0 … 990
  };
}

const SVG_NS = "http://www.w3.org/2000/svg";

function svgEl(name, attrs) {
  const el = document.createElementNS(SVG_NS, name);
  if (attrs) {
    for (const k in attrs) el.setAttribute(k, attrs[k]);
  }
  return el;
}

function parseJSON(raw, label) {
  try {
    return JSON.parse(raw || "[]");
  } catch (e) {
    console.error(`svg-map: ${label} payload invalid`, e);
    return [];
  }
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/* ── Tooltip-Manager (ein globaler State pro Map-Container) ──────────── */

/**
 * Erstellt einen Tooltip-Manager für einen einzelnen Map-Container.
 *
 * Konzept:
 *   activePin  — das SVG <g>-Element, dessen Tooltip gerade "sticky" (gepinnt) ist.
 *   hoverPin   — das SVG <g>-Element, über dem die Maus gerade ist (für Hover-Preview).
 *
 * Sticky = durch Klick/Enter/Space geöffnet → bleibt offen bis:
 *   - erneuter Klick auf denselben Pin
 *   - Klick auf anderen Pin (der öffnet sich stattdessen)
 *   - Klick außerhalb des Containers
 *   - Escape-Taste
 *
 * Hover-Preview = durch mouseenter, nur wenn kein sticky-Pin aktiv ist →
 *   schließt bei mouseleave wieder.
 */
function createTooltipManager(container) {
  let tip = container.querySelector(".ds-map__tooltip");
  if (!tip) {
    tip = document.createElement("div");
    tip.className = "ds-map__tooltip";
    tip.setAttribute("role", "tooltip");
    tip.hidden = true;
    container.appendChild(tip);
  }

  let activePin = null; // sticky-geöffneter Pin
  let hoverPin = null;  // aktuell gehoverter Pin (kein sticky)

  function positionTooltip(target) {
    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    let left = tRect.left - cRect.left + tRect.width / 2;
    let top = tRect.top - cRect.top;
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;

    // Horizontal innerhalb des Containers halten.
    requestAnimationFrame(() => {
      const own = tip.getBoundingClientRect();
      const overRight = own.right - cRect.right;
      const overLeft = cRect.left - own.left;
      if (overRight > 0) tip.style.left = `${left - overRight - 8}px`;
      else if (overLeft > 0) tip.style.left = `${left + overLeft + 8}px`;
    });
  }

  function showTip(pin, html) {
    tip.innerHTML = html;
    tip.hidden = false;
    positionTooltip(pin);
  }

  function hideTip() {
    tip.hidden = true;
    tip.innerHTML = "";
  }

  function setActivePin(pin) {
    // Alten aktiven Pin zurücksetzen
    if (activePin && activePin !== pin) {
      activePin.setAttribute("aria-expanded", "false");
    }
    activePin = pin;
    if (pin) {
      pin.setAttribute("aria-expanded", "true");
    }
  }

  /** Sticky öffnen / toggling */
  function openSticky(pin, html) {
    if (activePin === pin) {
      // Zweiter Klick = schließen
      setActivePin(null);
      hideTip();
      return;
    }
    setActivePin(pin);
    showTip(pin, html);
  }

  /** Nur-Hover (kein sticky aktiv) */
  function openHover(pin, html) {
    if (activePin) return; // sticky hat Vorrang
    hoverPin = pin;
    showTip(pin, html);
  }

  /** Hover verlassen (nur wenn nicht sticky) */
  function closeHover(pin) {
    if (activePin) return; // sticky bleibt
    if (hoverPin === pin) {
      hoverPin = null;
      hideTip();
    }
  }

  /** Alles schließen (Escape / Außenklick) */
  function closeAll() {
    if (activePin) activePin.setAttribute("aria-expanded", "false");
    activePin = null;
    hoverPin = null;
    hideTip();
  }

  // Außenklick: Wenn außerhalb des Containers geklickt wird → schließen.
  // Wird nur registriert, wenn etwas aktiv ist (Perf-Optimierung via capture).
  function onDocClick(ev) {
    if (!container.contains(ev.target)) {
      closeAll();
    }
  }
  document.addEventListener("click", onDocClick, true);

  // Escape schließt aktiven Tooltip.
  function onKeyDown(ev) {
    if (ev.key === "Escape" && activePin) {
      ev.stopPropagation();
      const prev = activePin;
      closeAll();
      // Fokus zurück auf den Pin
      prev.focus();
    }
  }
  container.addEventListener("keydown", onKeyDown);

  return { openSticky, openHover, closeHover, closeAll };
}

/* ── Projektkarte ──────────────────────────────────────────────────── */

function buildProjectMap(container) {
  const svg = container.querySelector(".ds-map__svg");
  if (!svg) return;
  const pinsGroup = svg.querySelector(".ds-map__pins");
  if (!pinsGroup) return;

  const pins = parseJSON(container.dataset.projectmapPins, "projectmap pins");
  if (pins.length === 0) return;

  const tm = createTooltipManager(container);
  const root = getComputedStyle(document.documentElement);
  const bg = root.getPropertyValue("--c-bg").trim() || "#FBF8F2";

  // Touch-Detection: auf Touch-Geräten gibt es kein Hover — Klick ist primär.
  const isTouch = window.matchMedia("(hover: none)").matches;

  pins.forEach((p) => {
    const lat = parseFloat(p.lat);
    const lng = parseFloat(p.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    const { x, y } = project(lng, lat);

    const isDone = p.status === "abgeschlossen";
    const statusKey = isDone ? "abgeschlossen" : "laufend";
    const statusLabel = isDone ? "Abgeschlossen" : "Laufend";

    const g = svgEl("g", {
      class: `ds-map__pin ds-map__pin--${statusKey}`,
      transform: `translate(${x.toFixed(1)} ${y.toFixed(1)})`,
      role: "button",
      tabindex: "0",
      "aria-expanded": "false",
      "aria-label": `${p.title || "Projekt"}${
        p.location ? ", " + p.location : ""
      } — ${statusLabel}`,
    });

    // Halo + Punkt
    g.appendChild(svgEl("circle", { class: "ds-map__pin-halo", r: "18" }));
    g.appendChild(
      svgEl("circle", {
        class: "ds-map__pin-dot",
        r: "11",
        stroke: bg,
        "stroke-width": "3",
      }),
    );

    // Tooltip-HTML — Titel prominent, dann Ort / Status / Link
    let html = `<strong class="ds-map__tip-title">${escapeHtml(p.title)}</strong>`;
    if (p.location)
      html += `<span class="ds-projectmap__pop-loc">${escapeHtml(p.location)}</span>`;
    html += `<span class="ds-projectmap__pop-status" data-status="${statusKey}">${statusLabel}</span>`;
    if (p.url)
      html += `<a class="ds-projectmap__pop-link" href="${escapeHtml(p.url)}">Mehr erfahren →</a>`;

    // Hover-Events nur auf Nicht-Touch-Geräten
    if (!isTouch) {
      g.addEventListener("mouseenter", () => tm.openHover(g, html));
      g.addEventListener("mouseleave", () => tm.closeHover(g));
    }

    // Klick: sticky toggling (immer, auch auf Touch)
    g.addEventListener("click", (ev) => {
      ev.stopPropagation(); // Außenklick-Handler nicht auslösen
      tm.openSticky(g, html);
    });

    // Tastatur
    g.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        ev.stopPropagation();
        // Enter mit URL: direkt navigieren; ohne URL: sticky toggling.
        if (ev.key === "Enter" && p.url) {
          window.location.href = p.url;
        } else {
          tm.openSticky(g, html);
        }
      }
      // Escape wird vom Container-keydown-Handler erledigt (bubbling).
    });

    pinsGroup.appendChild(g);
  });
}

/* ── Reisekarte (nummerierte Etappen) ──────────────────────────────── */

function buildTravelMap(container) {
  const svg = container.querySelector(".ds-map__svg");
  if (!svg) return;
  const pinsGroup = svg.querySelector(".ds-map__pins");
  const linesGroup = svg.querySelector(".ds-map__lines");
  if (!pinsGroup) return;

  const stations = parseJSON(
    container.dataset.travelmapStations,
    "travelmap stations",
  );
  if (stations.length === 0) return;

  const tm = createTooltipManager(container);
  const root = getComputedStyle(document.documentElement);
  const bg = root.getPropertyValue("--c-bg").trim() || "#FBF8F2";

  const isTouch = window.matchMedia("(hover: none)").matches;

  const pts = [];
  stations.forEach((s) => {
    const lat = parseFloat(s.lat);
    const lon = parseFloat(s.lon);
    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      pts.push(null);
      return;
    }
    pts.push(project(lon, lat));
  });

  // Verbindungslinie zwischen aufeinanderfolgenden Stationen.
  const valid = pts.filter(Boolean);
  if (linesGroup && valid.length >= 2) {
    const d = valid
      .map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`)
      .join(" ");
    linesGroup.appendChild(svgEl("path", { class: "ds-map__route", d }));
  }

  stations.forEach((s, idx) => {
    const pt = pts[idx];
    if (!pt) return;

    const g = svgEl("g", {
      class: "ds-map__pin ds-map__pin--station",
      transform: `translate(${pt.x.toFixed(1)} ${pt.y.toFixed(1)})`,
      role: "button",
      tabindex: "0",
      "aria-expanded": "false",
      "aria-label": `Station ${idx + 1}: ${s.name || ""}${
        s.date ? " (" + s.date + ")" : ""
      }`,
    });
    g.appendChild(
      svgEl("circle", {
        class: "ds-map__pin-dot",
        r: "14",
        stroke: bg,
        "stroke-width": "3",
      }),
    );
    const label = svgEl("text", {
      class: "ds-map__pin-num",
      x: "0",
      y: "0",
      "text-anchor": "middle",
      "dominant-baseline": "central",
    });
    label.textContent = String(idx + 1);
    g.appendChild(label);

    let html = `<strong class="ds-map__tip-title">${escapeHtml(s.name)}</strong>`;
    if (s.date)
      html += `<span class="ds-travelmap__pop-date">${escapeHtml(s.date)}</span>`;

    if (!isTouch) {
      g.addEventListener("mouseenter", () => tm.openHover(g, html));
      g.addEventListener("mouseleave", () => tm.closeHover(g));
    }

    g.addEventListener("click", (ev) => {
      ev.stopPropagation();
      tm.openSticky(g, html);
    });

    g.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        ev.stopPropagation();
        tm.openSticky(g, html);
      }
    });

    pinsGroup.appendChild(g);
  });
}

/* ── Init ──────────────────────────────────────────────────────────── */

export function initProjectMap() {
  document
    .querySelectorAll(".ds-projectmap")
    .forEach((c) => buildProjectMap(c));
}

export function initTravelMap() {
  document.querySelectorAll(".ds-travelmap").forEach((c) => buildTravelMap(c));
}

/** Beide auf einmal (No-Op, wenn keine Map im DOM). */
export function init() {
  initProjectMap();
  initTravelMap();
}
